import { useRef, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TreemapModule from "highcharts/modules/treemap";
import DrillDownModule from "highcharts/modules/drilldown";

TreemapModule(Highcharts);
DrillDownModule(Highcharts);

const chartStyle = {
  fontSize: "13px",
  fontWeight: 600,
  textDecoration: "none",
  fontFamily: "Verdana, sans-serif",
};

const TreeMap = ({
  chartTitle,
  treeData,
  drilldownSeries = [],
  extraMargin,
  onClick,
  chartColor="#481A6C",
  selectedPointColor = "#481A6C99",
  selectedCompanyName = null,
  onDoubleClick = () => null,
}) => {
  const clickTimeout = useRef(null);
  const chartComponentRef = useRef(null);
  useMemo(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
  
    chart.series?.[0]?.data?.forEach((point) => {
      const color = point.name === "Other" ? "#5757D6" : chartColor;
      point.update(
        { color },
        selectedCompanyName ? false : true
      );
    });
  
    if (selectedCompanyName) {
      const selectedPoint = chart.series[0].data.find(
        (p) => p.name === selectedCompanyName
      );
  
      if (selectedPoint) {
        selectedPoint.update(
          {
            color: selectedPointColor,
          },
          true
        );
      }
    }
  }, [selectedCompanyName, chartColor, selectedPointColor]);

  const onClickCatgory = function (event) {
    event.preventDefault();
    const point = event.point;
    const newSeriesId = point.drilldown;

    if (point.name) {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
        onDoubleClick(point.name);
      } else {
        clickTimeout.current = setTimeout(() => {
          clickTimeout.current = null;
          if (!newSeriesId) {
            onClick(point.name);
          }
        }, 250);
      }
    } else {
      onClick(null);
    }
  };

  const options = useMemo(
    () => ({
      chart: {
        marginLeft: extraMargin,
        marginRight: extraMargin,
        events: {
          drillup: () => {
            onClick("");
          },
          drilldown: () => {
            onClick("");
          },
        },
      },
      series: [
        {
          name: "All",
          type: "treemap",
          layoutAlgorithm: "squarified",
          allowDrillToNode: true,
          animationLimit: 1000,
          dataLabels: {
            enabled: false,
            color: "#ffffff",
            useHTML: true,
            allowOverlap: true,
            style: chartStyle,
          },
          cursor: "pointer",
          point: {
            events: {
              click: onClickCatgory,
            },
          },
          levels: [
            {
              level: 1,
              dataLabels: {
                enabled: true,
                color: "#ffffff",
                useHTML: true,
                allowOverlap: true,
                style: chartStyle,
              },
              borderWidth: 3,
              levelIsConstant: false,
            },
            {
              level: 2,
              dataLabels: {
                enabled: true,
              },
              borderWidth: 3,
              levelIsConstant: false,
            },
          ],
          accessibility: {
            exposeAsGroupOnly: true,
          },
          data: treeData,
          color: chartColor,
        },
      ],
      drilldown: {
        activeDataLabelStyle: {
          color: "#ffffff",
          ...chartStyle,
        },
        series: [
          {
            ...drilldownSeries,
            dataLabels: {
              enabled: true,
              color: "#ffffff",
              useHTML: true,
              allowOverlap: true,
              style: chartStyle,
            },
            cursor: "pointer",
            point: {
              events: {
                click: onClickCatgory,
              },
            },
          },
        ],
      },
      title: {
        text: chartTitle,
        align: "left",
        style: {
          fontSize: "13px",
          fontFamily: "Verdana, sans-serif",
        },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        backgroundColor: "#ffffff",
        formatter: function () {
          if (this.point.name === "Other") {
            return `<b>${this.point.name}</b>: < 1%<br/>`;
          } else {
            return `<b>${this.point.name}</b>: ${this.point.value}%<br/>`;
          }
        }
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
    }),
    // eslint-disable-next-line
    [treeData, chartColor, selectedPointColor]
  );

  return (
    <div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </div>
  );
};

export default TreeMap;
